import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Database, GitMerge, Search, Layers, CheckCircle2, Zap, BarChart3 } from "lucide-react";

const offerings = [
  {
    icon: Database,
    title: "Проектирование дата платформы",
    description:
      "Помогаем выбрать архитектуру хранилища под ваши объёмы и паттерны запросов: от выбора движков и схем партиционирования до стратегии шардирования и репликации.",
    points: [
      "Аудит текущей архитектуры и узких мест",
      "Проектирование схем под аналитические нагрузки",
      "Стратегия партиционирования и первичных ключей",
      "Настройка репликации и шардирования",
    ],
  },
  {
    icon: GitMerge,
    title: "Внедрение ClickHouse",
    description:
      "Сопровождаем миграцию с других систем (PostgreSQL, Redshift, BigQuery, Vertica) — от прототипа до production. Помогаем избежать типичных ошибок первого внедрения.",
    points: [
      "Планирование и roadmap миграции",
      "Перенос схем и исторических данных",
      "Интеграция с существующим стеком (Kafka, Spark, Airflow)",
      "Тестирование производительности и нагрузочное тестирование",
    ],
  },
  {
    icon: Search,
    title: "Оптимизация производительности",
    description:
      "Разбираем медленные запросы и проблемы с производительностью кластера. Работаем с профилями выполнения, индексами и настройками движков.",
    points: [
      "Анализ system.query_log и flame graph",
      "Оптимизация первичных и вторичных индексов",
      "Рефакторинг схем и Materialized Views",
      "Настройка сжатия и TTL-политик",
    ],
  },
  {
    icon: Layers,
    title: "Data Pipeline и интеграции",
    description:
      "Проектируем и оптимизируем потоковую и батч-инженерию: приём данных, трансформации, версионирование и мониторинг качества данных.",
    points: [
      "Kafka → ClickHouse: надёжный стриминг",
      "Проектирование ETL/ELT пайплайнов",
      "dbt + ClickHouse: лучшие практики",
      "Мониторинг и алертинг на аномалии данных",
    ],
  },
];

const useCases = [
  { icon: BarChart3, label: "Стартапы", description: "Выбирают стек и строят MVP дата платформы" },
  { icon: Database, label: "Scaleup-компании", description: "Перерастают PostgreSQL и переходят на OLAP" },
  { icon: GitMerge, label: "Enterprise", description: "Мигрируют с дорогих облачных хранилищ" },
  { icon: Zap, label: "Data-команды", description: "Оптимизируют существующие кластеры" },
];

export const ConsultingPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-full bg-neutral-light overflow-y-auto">
      {/* Hero */}
      <div className="relative bg-neutral-800 overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)`,
            backgroundSize: "28px 28px",
          }}
        />
        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-indigo-400/15 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-12 -left-12 w-48 h-48 rounded-full bg-primary/10 blur-3xl pointer-events-none" />

        <div className="relative z-10 max-w-3xl mx-auto px-6 py-12">
          <Link
            to="/courses"
            className="inline-flex items-center gap-1.5 text-white/60 hover:text-white text-sm mb-6 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Назад
          </Link>

          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold bg-indigo-400/20 text-indigo-300 border border-indigo-400/30">
              <Database className="h-3 w-3" />
              Консалтинг
            </span>
          </div>

          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3 leading-snug">
            Проектирование и внедрение дата платформ
          </h1>
          <p className="text-white/65 text-base leading-relaxed max-w-xl">
            Помогаем командам выбрать правильную архитектуру, внедрить ClickHouse без потерь и выжать максимум из существующей инфраструктуры.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 py-10 space-y-8">

        {/* Offerings */}
        <section className="space-y-4">
          <h2 className="text-xs font-semibold text-neutral-text/40 uppercase tracking-wider">
            Направления работы
          </h2>
          {offerings.map((item) => (
            <div
              key={item.title}
              className="bg-white rounded-2xl p-6 border border-neutral-border shadow-sm"
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center border border-indigo-100">
                  <item.icon className="w-4 h-4 text-indigo-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-neutral-text">{item.title}</h3>
                  <p className="text-sm text-neutral-text/65 mt-0.5 leading-relaxed">{item.description}</p>
                </div>
              </div>
              <ul className="grid sm:grid-cols-2 gap-2 mt-4">
                {item.points.map((point) => (
                  <li key={point} className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-neutral-text/70 leading-snug">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>

        {/* Who it's for */}
        <section className="bg-white rounded-2xl p-6 border border-neutral-border shadow-sm">
          <h2 className="text-xs font-semibold text-neutral-text/40 uppercase tracking-wider mb-4">
            С кем работаем
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {useCases.map((uc) => (
              <div key={uc.label} className="flex items-start gap-2.5">
                <uc.icon className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-neutral-text leading-none mb-0.5">{uc.label}</p>
                  <p className="text-xs text-neutral-text/55">{uc.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Expertise note */}
        <p className="text-xs text-neutral-text/40 italic px-1">
          Опыт включает проектирование систем с нагрузкой от 10 млрд событий в сутки и успешные миграции с PostgreSQL, Redshift и Vertica.
        </p>

        {/* CTA */}
        <div className="bg-neutral-800 rounded-2xl p-6 relative overflow-hidden">
          <div
            className="absolute inset-0 opacity-[0.06]"
            style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)`,
              backgroundSize: "20px 20px",
            }}
          />
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-indigo-400/15 blur-2xl pointer-events-none" />
          <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="font-semibold text-white mb-1">Обсудить проект</p>
              <p className="text-sm text-white/55 leading-relaxed max-w-sm">
                Расскажите о задаче — разберём архитектуру и предложим план действий.
              </p>
            </div>
            <a
              href="https://t.me/quantile_camp"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0 flex items-center gap-2 px-6 py-3 rounded-xl bg-indigo-500 hover:bg-indigo-400 text-white text-sm font-semibold transition-colors shadow-sm"
            >
              Написать в Telegram
            </a>
          </div>
        </div>

      </div>
    </div>
  );
};
